import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import PortraitFrame from "./PortraitFrame";
import { HERO, IMAGES } from "@/config";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16"
    >
      <motion.div
        style={{ opacity: glowOpacity }}
        className="absolute top-[-20%] right-[-10%] h-[70vh] w-[70vh] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12)_0%,transparent_65%)] pointer-events-none"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-12 bg-gold" />
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
              {HERO.eyebrow}
            </span>
          </motion.div>

          <h1 className="mt-7 font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-[4.4rem] leading-[1.08] text-cream">
            {HERO.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-gold" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="mt-7 max-w-xl text-mist text-base md:text-lg leading-relaxed"
          >
            {HERO.subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#egitimler"
              data-testid="hero-cta-trainings"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(212,175,55,0.4)]"
            >
              Eğitimleri İncele
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </a>
            <a
              href="#danismanlik"
              data-testid="hero-cta-consultation"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-gold/50 px-8 py-4 text-sm font-semibold tracking-wide text-cream transition-all duration-300 hover:border-gold hover:bg-gold/10"
            >
              Danışmanlık Al
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-12 flex gap-10 sm:gap-14"
          >
            {HERO.stats.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                <div className="font-serif text-3xl sm:text-4xl text-gold">{s.value}</div>
                <div className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-mist">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: portraitY }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
          className="lg:col-span-5 max-w-md mx-auto w-full lg:max-w-none"
        >
          <PortraitFrame src={IMAGES.heroPortrait} testId="hero-portrait" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-mist"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Keşfet</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown className="w-4 h-4 text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
